/**
 * The favourites list as ARITHMETIC — what is stored, and what a move does to it.
 *
 * It is a file of its own, with no imports at all, for two reasons.
 *
 * `libs/nav.ts` is a singleton built over the router, the i18n instance and two Pinia
 * stores, so nothing in it can be exercised without starting an application. The
 * ordering, though, is the part that is easy to get wrong — an index taken from the
 * list somebody can SEE, applied to the list that is STORED, is off by one for every
 * hidden entry above it — and it is pure: a list of route names in, a list of route
 * names out. Kept here it is checked by `tools/favourites.test.mjs` on every `yarn
 * test`, which is the only kind of test this project has for a frontend.
 *
 * # The storage format
 *
 * `pms:nav-favourites:<account>` holds a JSON ARRAY of route names, and the array's
 * order IS the order they are drawn in. That is what it has held since the list landed
 * (v1.541.0), so there is nothing to migrate: an array was ordered before anybody could
 * reorder it, and the only thing v1.545.0 changes is that the order is now somebody's
 * decision rather than the order they happened to press the stars in.
 *
 * Null — no key at all — is not an empty list. Null means "this account has never said",
 * and takes the defaults for its role; an empty array is somebody who unpinned the lot
 * and is entitled to a menu with no favourites in it.
 */

/**
 * The stored value read as a list, or null when there is nothing usable there.
 *
 * Tolerant on purpose: this is `localStorage`, which anything on the machine can write,
 * and a sidebar that throws is worse than a sidebar that forgot. Anything that is not a
 * string is dropped, and so is a REPEAT — a name twice over would draw two rows with
 * the same key, and a move addressed by name could then mean either of them.
 */
export const parseFavourites = (raw: string): string[] | null => {
    if (!raw) return null

    try {
        const parsed: unknown = JSON.parse(raw)

        if (!Array.isArray(parsed)) return null

        const names = parsed.filter((name): name is string => typeof name === 'string')

        return [...new Set(names)]
    } catch {
        return null
    }
}

/** The list as it is written back. One place, so the read above is its exact opposite. */
export const serialiseFavourites = (names: string[]): string => JSON.stringify(names)

/**
 * `moved` taken out and put back in immediately before `before` — or at the end when
 * `before` is null.
 *
 * Addressed by NAME rather than by index, and that is the whole point of the signature.
 * The sidebar draws only the favourites this account can still see, so its third row
 * can be the stored list's fifth entry; an index would move the wrong one the moment a
 * permission was withdrawn. A name is the same name in both lists, and the entries this
 * account cannot see keep their places around it.
 *
 * The list is returned unchanged — the same reference — when the move would not move
 * anything, so a caller can persist unconditionally without writing an identical list.
 */
export const moveFavourite = (names: string[], moved: string, before: string | null): string[] => {
    if (moved === before || !names.includes(moved)) return names

    const rest = names.filter(name => name !== moved)
    const at   = before === null ? rest.length : rest.indexOf(before)

    // A `before` that is not in the list is a stale drop target — the list changed under
    // the drag — and doing nothing is the honest answer to it.
    if (at < 0) return names

    if (at === names.indexOf(moved)) return names

    rest.splice(at, 0, moved)

    return rest
}

/**
 * What `moveFavourite`'s `before` has to be for "one step up" or "one step down" of the
 * entry at `index` of the VISIBLE list — or undefined when there is no step to take.
 *
 * Up is simple: insert before the row above. Down is the one that reads oddly and is
 * the reason this is a function rather than two lines at the call site — "after the row
 * below" is "before the row below THAT", and when there is no row below that, it is the
 * end of the list (null).
 */
export const nudgeTarget = (visible: string[], index: number, by: -1 | 1): string | null | undefined => {
    if (index < 0 || index >= visible.length) return undefined

    if (by < 0) return index === 0 ? undefined : visible[index - 1]

    if (index >= visible.length - 1) return undefined

    return index + 2 < visible.length ? visible[index + 2] : null
}

/**
 * Where a drop at `dropIndex` of the visible list lands, as a `before` name.
 *
 * `dropIndex` counts the GAPS and not the rows: 0 is above the first row and
 * `visible.length` is below the last one, which is the end of the list and therefore
 * null.
 */
export const dropTarget = (visible: string[], dropIndex: number): string | null =>
    visible[dropIndex] ?? null
